import { createPortal } from "react-dom";
import Button from "../../../../shared/components/Button";
import InfoTooltip from "../../../../shared/components/InfoTooltip";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { ProfileSharePreview } from "../utils/profileShareCard";

type ProfileShareModalProps = {
  open: boolean;
  preview: ProfileSharePreview | null;
  loading: boolean;
  status: string;
  dashboardTexts: typeof siteTexts.profile.user.dashboard;
  info: typeof siteTexts.common.info;
  onClose: () => void;
  onShare: () => void;
  onDownload: () => void;
  onCopyLink: () => void;
};

export default function ProfileShareModal({
  open,
  preview,
  loading,
  status,
  dashboardTexts,
  info,
  onClose,
  onShare,
  onDownload,
  onCopyLink,
}: ProfileShareModalProps) {
  if (!open) return null;

  return createPortal(
    <div
      className="profile-share-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={dashboardTexts.share.dialogAria}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="profile-share-modal">
        <div className="profile-share-header">
          <div>
            <strong className="info-label">
              {dashboardTexts.share.title}
              <InfoTooltip content={info.shareResult} />
            </strong>
            <span>{dashboardTexts.share.description}</span>
          </div>
          <button
            type="button"
            className="profile-share-close"
            onClick={onClose}
            aria-label={dashboardTexts.share.closeAria}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="profile-share-content">
          <div className="profile-share-preview">
            {preview ? (
              <img src={preview.url} alt={dashboardTexts.share.previewAlt} />
            ) : (
              <div className="profile-share-loading">
                {loading
                  ? dashboardTexts.share.generating
                  : dashboardTexts.share.unavailable}
              </div>
            )}
          </div>

          <div className="profile-share-actions">
            <Button
              type="button"
              variant="primary"
              onClick={onShare}
              disabled={!preview}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                share
              </span>
              {dashboardTexts.share.share}
            </Button>
            <Button
              type="button"
              variant="pill"
              onClick={onDownload}
              disabled={!preview}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                download
              </span>
              {dashboardTexts.share.downloadPng}
            </Button>
            <Button type="button" variant="pill" onClick={onCopyLink}>
              <span className="material-symbols-outlined" aria-hidden="true">
                content_copy
              </span>
              {dashboardTexts.share.copyLink}
            </Button>
            {status && <p className="profile-share-status">{status}</p>}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
